import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Checkbox,
  Chip,
  createFilterOptions,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useTranslation } from "i18n-fe";
import { RideStatusBadge, TableSkeleton, useApiResource } from "common-fe";
import {
  RIDE_STATUSES,
  DERIVED_RIDE_STATUSES,
  DERIVED_CLASS_STATUSES,
  canAddManualRide,
  deriveClassStatus,
  formatPercentage,
  classifyDshbClassKind,
  isDshbGridKind,
  isRealTestSlug,
  resolveConfirmedDshbClassKind,
  DSHB_CLASS_KINDS,
  PROSPECTS_IN_HAND_FVD_TEST_ID,
} from "score-common";
import AppShell from "./AppShell";
import PageBreadcrumbs from "./PageBreadcrumbs";
import SplitClassChip from "./SplitClassChip";
import { useRidesForClass } from "../hooks/useRidesForClass";
import { useClassGridTest } from "../hooks/useClassGridTest";
import { useRingState } from "../hooks/useRingState";
import { useScoreApi, unwrap } from "../api/scoreApi";
import { useCatalogDefs } from "../hooks/useCatalogDefs";
import { useScoreContext } from "../context/ScoreContext";
import {
  addManualRide,
  checkManualRideDiscard,
  discardManualRide,
  subscribeManualRides,
} from "../store/manualRides";
import {
  classNumberLabel,
  formatVenueTime,
  resolveCatalogTestName,
  rideTestName,
  sortByScheduledTime,
} from "../util/classDisplay";
import { rideCnoNumber } from "../util/rideDisplay";

// #204 — cap free-text entity names so a slip on the keyboard can't
// stuff a paragraph into a horse/rider field.
const MAX_ENTITY_NAME = 60;

const entityFilter = createFilterOptions();

// #204 — a horse/rider picker that searches existing entries and, when
// the typed value matches none, surfaces an "Add …" option that opens
// the add-new-entity dialog (instead of silently accepting free text).
function EntityField({ label, options, value, onChange, onRequestAdd }) {
  return (
    <Autocomplete
      // #253 — freeSolo + autoSelect so a name the scribe TYPES is
      // committed on blur/submit, not silently dropped. Previously this
      // only captured a value when an option was explicitly clicked, so
      // a typed horse/rider name never reached the manual ride and the
      // header chips came up empty. The "Add …" affordance (#204) still
      // works for an explicit add; this just stops losing typed names.
      freeSolo
      autoSelect
      options={options}
      value={value || null}
      onChange={(_e, newVal) => {
        if (newVal == null) {
          onChange("");
        } else if (typeof newVal === "string") {
          onChange(newVal);
        } else if (newVal.addValue) {
          onRequestAdd(newVal.addValue);
        }
      }}
      getOptionLabel={(opt) =>
        typeof opt === "string" ? opt : opt?.label ?? ""
      }
      isOptionEqualToValue={(opt, val) =>
        (typeof opt === "string" ? opt : opt?.value) === val
      }
      filterOptions={(opts, params) => {
        const filtered = entityFilter(opts, params);
        const input = params.inputValue.trim();
        const exists = opts.some(
          (o) => typeof o === "string" && o.toLowerCase() === input.toLowerCase(),
        );
        if (input !== "" && !exists) {
          filtered.push({ addValue: input, label: `Add “${input}”` });
        }
        return filtered;
      }}
      handleHomeEndKeys
      selectOnFocus
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          inputProps={{ ...params.inputProps, maxLength: MAX_ENTITY_NAME }}
          sx={READABLE_TEXTFIELD_SX}
        />
      )}
    />
  );
}

// A ride reads as "scored" once at least one sheet is complete — COMPLETED
// (some judges) or FINAL (all judges). Both surface the percentage + green text.
const isScoredStatus = (status) =>
  status === DERIVED_RIDE_STATUSES.COMPLETED || status === DERIVED_RIDE_STATUSES.FINAL;

function scoreLabel(t, ride) {
  if (ride.status === RIDE_STATUSES.SCRATCHED) return t("rides.scratched");
  if (isScoredStatus(ride.status) && typeof ride.percentage === "number") {
    return t("rides.scoreCompleted", {
      percentage: formatPercentage(ride.percentage),
    });
  }
  if (ride.status === RIDE_STATUSES.IN_PROGRESS) return t("rides.scoreInProgress");
  // #3099 - marks are flagged, never hidden, on an ended ride awaiting signature.
  if (ride.status === DERIVED_RIDE_STATUSES.NEEDS_SIGNING) return t("rides.scoreNeedsSigning");
  return t("rides.scoreNotStarted");
}

// #203 — local contrast fix for MUI's outlined TextField. The brand
// theme's primary.main (#363636 darkGrey) is invisible on the dark
// dialog surface in dark mode, so the focused outline + label
// disappear. Force both to text.primary while focused so the field
// reads cleanly. (Theme-level fix to brand.primary for dark mode is
// a broader change tracked separately.)
const READABLE_TEXTFIELD_SX = {
  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "text.primary",
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: "text.primary",
  },
};

export default function RideList() {
  const navigate = useNavigate();
  const location = useLocation();
  const { classId } = useParams();
  const { t } = useTranslation("score");
  const { data, settled, refetch } = useRidesForClass(classId);
  const klass = data?.klass;
  const rides = data?.rides ?? [];

  // #2783 (bug #257) — one-shot confirmation from the scoresheet's
  // Scratch / No Show term-reason flow, which lands here (replace:true) with
  // `state.rideTermConfirmation = { number, reason }` after marking the ride.
  // Captured into local state at mount so it survives the history-entry
  // scrub below, then the scrubbed state means navigating back from a later
  // ride never re-shows a stale banner.
  const [termConfirmation, setTermConfirmation] = useState(
    () => location.state?.rideTermConfirmation ?? null
  );
  useEffect(() => {
    if (!location.state?.rideTermConfirmation) return;
    setTermConfirmation(location.state.rideTermConfirmation);
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.state, location.pathname, navigate]);

  // #1750 — a class-grid test (DSE/DSM equitation) sends the scribe to the
  // spreadsheet grid instead of the per-ride list. Gated on sheetLayout, not
  // any other property of the class, and never for a movement-grid test
  // (today's behaviour for every other def, including undefined/legacy).
  //
  // #2183 — a CHAMPIONSHIP/SUITABILITY class routes to the grid REGARDLESS
  // of routingTest.sheetLayout: FVD stamps these classes with their
  // qualifying class's Test id (score-common/src/utils/dshb-class-kind.js),
  // so routingTest.sheetLayout here is the QUALIFYING class's own layout
  // ("movement-grid" for the observed WEC/Del Mar in-hand tests), never
  // "class-grid". The kind must be resolved fresh off the CLASS's
  // className, never off the reused Test row.
  //
  // #2184 — MATERIALE/UNDER_SADDLE (DR211.1, DR211.4-scored, Jaybill's
  // 2026-09-01 decision to always use the class sheet for Under Saddle
  // regardless of size) route the same way, for the same reason: their own
  // real Test row's sheetLayout is never authored "class-grid" either.
  // isDshbGridKind is the shared root for BOTH grid families (score-common's
  // dshb-class-kind.js) — see that file's own comment for why this check
  // must not be re-derived inline here.
  const { test: routingTest, loading: routingTestLoading } = useClassGridTest(klass, rides);
  // #2277 — the stored per-class override wins over the className heuristic.
  const dshbKind = classifyDshbClassKind({
    className: klass?.className,
    fvdTestId: routingTest?.fvdId,
    dshbKindOverride: klass?.dshbClassKindOverride,
  });
  // #2745 - a plain CHAMPIONSHIP classification alone is NOT enough to route
  // to the place-only grid, because CHAMPIONSHIP_NAME_PATTERN matches the
  // class NAME with no DSHB anchor of its own (same false-positive #2743
  // already fixed for the Handler/Rider label - see
  // resolveConfirmedDshbClassKind's own doc comment for the full citation
  // and the shared three-tier confirmation this now runs before routing).
  const confirmedDshbKind = resolveConfirmedDshbClassKind({
    kind: dshbKind,
    fvdTestId: routingTest?.fvdId,
    test: routingTest,
    dshbKindOverride: klass?.dshbClassKindOverride,
  });
  const isDshbGridClass = isDshbGridKind(confirmedDshbKind);
  // #2202 (judge review, finding 1) - `routingTest` resolves a tick AFTER
  // `klass` (useCachedResource's cold mount starts at data:undefined, #2821,
  // and testId here is keyed off rides[0].testId, so it necessarily depends on
  // rides having loaded first). Before this test lands, classifyDshbClassKind
  // sees fvdTestId:undefined, and a USDFBC Series Final's championship-shaped
  // name then classifies as plain CHAMPIONSHIP (the USDFBC_ELIGIBLE_FVD_TEST_
  // IDS AND-gate in dshb-class-kind.js can't pass with no id yet), which is
  // also grid-routed - so without this gate, every Series Final mount
  // redirects to /grid, ClassGrid.jsx:108 bounces it straight back once ITS
  // OWN test resolves, and the scribe sees a flash or a ping-pong on every
  // load. Mirrors ClassGrid.jsx:108's own `!testLoading && test` gate so
  // both components require the SAME resolved-test precondition before
  // acting on isDshbGridClass.
  //
  // #2223 - the #2202 gate above only covers the LOADING window; it does
  // nothing once `routingTestLoading` settles to false with `routingTest`
  // still null - useCachedResource (common-fe) settles `loading:false` on
  // EVERY exit path, including both failure paths: fetchRemote throws with
  // nothing cached, or offline with a cold cache (see that file's own
  // comment). With `routingTest` permanently null, the exact same AND-gate
  // miss described above fires, and this time there is no later render to
  // correct it - the scribe lands on #2183's place-only grid for a class
  // USEF DR211.2 (docs/rules/usef/rulebook-dr-dressage-2026.pdf, (c) USEF
  // 2026, current edition per docs/rules/README.md) requires "individual
  // score sheets" for, with no way out: ClassGrid.jsx:108's own back-
  // redirect is gated on `!testLoading && test`, so a null `test` there
  // does NOT bounce it back either.
  //
  // Fix: before acting on a plain CHAMPIONSHIP classification, check
  // whether this className is EVEN CAPABLE of resolving to
  // USDFBC_CHAMPIONSHIP once a real fvdTestId is known - i.e. whether it
  // matches the championship+"Final" name shape - by probing
  // classifyDshbClassKind with a real (arbitrary) DSHB in-hand fvdTestId
  // from USDFBC_ELIGIBLE_FVD_TEST_IDS. This reuses dshb-class-kind.js's own
  // exported pure classifier as a name-shape probe, with no change to that
  // classifier at all - #2202 rejected changing classifyDshbClassKind's
  // RETURN BEHAVIOUR (making it refuse to downgrade a Final-named
  // championship when fvdTestId is unknown), which would have changed what
  // EVERY caller gets back, score-api's handlers included. This probe calls
  // the existing function completely unchanged, with a real id it already
  // accepts, and only reads whether that returns USDFBC_CHAMPIONSHIP; the
  // actual fvdTestId used for THIS render's real classification is
  // untouched. When the name IS shaped like a possible Series Final and
  // the real test hasn't resolved to anything, hold off rather than guess:
  // staying on this ordinary per-ride list is DR211.2's own required
  // outcome for a genuine Series Final, so the unresolved case defaults to
  // the safe answer instead of the place-only grid. An ordinary CHAMPIONSHIP/
  // SUITABILITY class (no "Final" in its name) never matches this probe, so
  // its routing is completely unchanged - it never depended on fvdTestId in
  // the first place.
  // #2277 — with an explicit override, the kind is already fully known
  // (dshbKind above reflects it), so this probe just checks the override
  // value directly instead of guessing from a synthetic fvdTestId.
  const couldBeUsdfbcFinal =
    classifyDshbClassKind({
      className: klass?.className,
      fvdTestId: PROSPECTS_IN_HAND_FVD_TEST_ID,
      dshbKindOverride: klass?.dshbClassKindOverride,
    }) === DSHB_CLASS_KINDS.USDFBC_CHAMPIONSHIP;
  useEffect(() => {
    if (routingTestLoading) return;
    if (!routingTest && couldBeUsdfbcFinal) return;
    if (routingTest?.sheetLayout === "class-grid" || isDshbGridClass) {
      navigate(`/classes/${classId}/grid`, { replace: true });
    }
  }, [routingTestLoading, routingTest, isDshbGridClass, couldBeUsdfbcFinal, classId, navigate]);

  // #1922 — a manual ride can be discarded from a SECOND place outside this
  // component's own tree: the unsynced-rides chip's popover
  // (UnsyncedRidesIndicator.jsx), which has no reference to this list's
  // `refetch` and can't call it directly. `useRidesForClass`'s cache is only
  // ever revalidated on mount/dep-change or an explicit refetch() — it has no
  // standing subscription of its own — so without this, a discard from the
  // chip clears the localStorage mirror and the IndexedDB rides row (see
  // store/manualRides.js discardManualRide) but this list's already-rendered
  // React state never learns about it, and the ride card sits there forever
  // with no reload. Every manual-rides write (add, discard, serverId stamp)
  // notifies here; refetch() re-reads the IndexedDB cache discardManualRide
  // just cleared, so the card drops with no page reload (AC3).
  useEffect(() => {
    let first = true;
    return subscribeManualRides(() => {
      if (first) { first = false; return; }   // hook already fetched on mount
      refetch();
    });
  }, [refetch]);

  // #380 — manual-ride dialog option sources come from real API data, not
  // mocks: test choices from the show's tests, and horse/rider suggestions
  // from the entrants already in this class (the scribe types a new name for
  // an entrant not listed — the field is free-solo).
  const api = useScoreApi();
  const { selectedShow } = useScoreContext();
  // #2081 (docs/score/ring-state.md) — Start class moved here from the
  // class list (#1935), beside Add Ride Manually. Same optional/additive
  // contract as before: a failed read or write is swallowed silently
  // (useRingState) and never gates anything else on this screen.
  const ringState = useRingState(selectedShow?.id);
  // #2081 — this page is already scoped to one class, so the class-level
  // status comes from the SAME derivation useClassStatuses uses on the
  // class list (deriveClassStatus, score-common), fed by the rides this
  // page already fetched — no extra request. Suppresses Start class on a
  // COMPLETE/PLACED class exactly as the old class-list action did.
  const classStatus = useMemo(() => deriveClassStatus(rides), [rides]);
  // #2976 -- a Region 7/CDS show comment on #2810 asked to disable Add
  // Manual Ride once the class is Placed/Finalized: adding a manual ride to
  // a class whose results are already locked corrupts them. canAddManualRide
  // (score-common) is the same client-side-pre-flight pattern as #2810's
  // pre-flight.js -- the button is disabled BEFORE the createRide op is ever
  // enqueued, not refused after a round trip. Deliberately narrow: this is
  // false ONLY once classStatus reaches PLACED, so the #386 off-daysheet
  // emergency manual-add path stays open for every other status (SCHEDULED,
  // IN_PROGRESS, COMPLETE-not-yet-placed) -- never a general lock.
  const manualRideAllowed = canAddManualRide(classStatus);
  const isClassCurrentInRing = klass?.ring
    ? ringState.isCurrentForRing(klass.ring, klass.id)
    : false;
  // card #1664 — table cache preferred over the bundled catalog for the
  // per-ride test-name chip below (rideTestName).
  const { catalogDefsById } = useCatalogDefs();
  const { data: showTests, loading: loadingTests } = useApiResource(
    async () =>
      selectedShow?.id ? unwrap(await api.tests.listTests(selectedShow.id)) : [],
    [api, selectedShow?.id]
  );
  // #1665 — the retired score-common/catalog bundle used to be the fallback
  // name source when catalogDefsById hadn't hydrated a given testId yet
  // (cold start / offline). showTests (above) is already fetched in this
  // component for the manual-add dialog's test picker, and it's the SAME
  // `tests` IndexedDB-backed store useScoreSheet.js reads for actual scoring
  // (never the bundle) — so it's strictly the right fallback source, not an
  // extra fetch.
  const showTestsById = useMemo(
    () => Object.fromEntries((showTests ?? []).map((t) => [t.id, t])),
    [showTests]
  );
  // #1878 — prefer the catalog's own clean name (name, stamped at reconcile
  // by catalogTestFields()) over FVD's raw testCode (daysheet.js:512-513
  // stamps the FVD day-sheet name onto testCode); fall back to testCode for a
  // row not yet re-reconciled or never matched. Same order as the manual-add
  // picker just below.
  const fallbackTestName = (testId) => {
    const t = showTestsById[testId];
    return t ? t.name ?? t.testCode ?? null : null;
  };
  const HORSE_NAMES = [
    ...new Set(rides.map((r) => r.horseName).filter((n) => n && n !== "—")),
  ];
  const RIDER_NAMES = [
    ...new Set(rides.map((r) => r.riderName).filter((n) => n && n !== "—")),
  ];

  // #189 — default to hiding completed rides; toggle to reveal.
  const [showCompleted, setShowCompleted] = useState(false);

  // #203 — manually add a ride for an emergency case where the
  // competitor isn't on the scheduled list. The new entry persists
  // via the localStorage manual-rides store and is picked up by
  // useScoreSheet on the next navigate.
  const [manualOpen, setManualOpen] = useState(false);
  const [manualCompetitor, setManualCompetitor] = useState("");
  const [manualTestId, setManualTestId] = useState("");
  const [manualHorse, setManualHorse] = useState("");
  const [manualRider, setManualRider] = useState("");
  const [manualError, setManualError] = useState(false);
  // #2103 — has the scribe explicitly acknowledged that the picked test
  // doesn't match this class's own testId? Resets whenever the test picker
  // changes, so acknowledging one mismatch never silently carries over to a
  // different one picked afterward.
  const [manualTestAck, setManualTestAck] = useState(false);
  // #204 — entities added on the fly this session, appended to the
  // lookup options so a freshly-created horse/rider is selectable.
  const [addedHorses, setAddedHorses] = useState([]);
  const [addedRiders, setAddedRiders] = useState([]);
  // #204 — add-new-entity dialog state. kind: "horse" | "rider".
  const [addEntity, setAddEntity] = useState({ open: false, kind: null, value: "" });

  // #1922 — discard-manual-ride dialog state. `guard` is
  // checkManualRideDiscard's preview (null while it's loading), so the
  // dialog knows up front whether this is a plain confirm, a refusal, or a
  // "will also lose X" second-tier confirm — before the scribe can commit to
  // anything. `discarding` guards the confirm button against a double-tap.
  const [discardTarget, setDiscardTarget] = useState(null);
  const [discardGuard, setDiscardGuard] = useState(null);
  const [discarding, setDiscarding] = useState(false);

  const openDiscard = (ride) => {
    setDiscardTarget(ride);
    setDiscardGuard(null);
    // #1922 judge fix — an IndexedDB read failure here used to leave
    // discardGuard permanently null, so the dialog stuck on its "…"
    // placeholder forever with no escape hatch on a kiosk. A rejection now
    // resolves to a definite, actionable refusal state instead.
    checkManualRideDiscard(ride.id)
      .then(setDiscardGuard)
      .catch(() => setDiscardGuard({ ok: false, reason: "check-failed" }));
  };
  const closeDiscard = () => {
    setDiscardTarget(null);
    setDiscardGuard(null);
    setDiscarding(false);
  };
  const confirmDiscard = async () => {
    if (!discardTarget || !discardGuard) return;
    setDiscarding(true);
    try {
      const force = discardGuard.reason === "has-score-data";
      const result = await discardManualRide(discardTarget.id, { force });
      if (result.ok) {
        closeDiscard();
        refetch();
      } else {
        // Guard state changed underneath us (e.g. a score save landed while
        // the dialog was open) — re-show the up-to-date refusal instead of
        // silently doing nothing.
        setDiscardGuard(result);
      }
    } finally {
      // #1922 judge fix — a rejected discardManualRide (e.g. IndexedDB
      // removal fails) used to skip this and leave the confirm button
      // disabled forever with no escape hatch on a kiosk.
      setDiscarding(false);
    }
  };

  const openManual = () => {
    // #2976 -- belt-and-braces: the button below is already disabled once
    // manualRideAllowed is false, so this is unreachable through the UI, but
    // guard the handler itself so nothing else (a stray keyboard trigger,
    // future callers) can open the dialog and enqueue a createRide op the
    // class no longer allows.
    if (!manualRideAllowed) return;
    setManualCompetitor("");
    // #462 — default the manual-ride test picker BLANK. In a Test-of-Choice
    // class the class test is meaningless, so pre-filling klass.testId is a
    // misleading default; the scribe picks the actual test explicitly.
    setManualTestId("");
    setManualHorse("");
    setManualRider("");
    setManualError(false);
    setManualTestAck(false);
    setManualOpen(true);
  };

  // #2103 — warn (not block, #386's emergency path + #435's "testId is only
  // a hint") when the picked test doesn't match this class's own testId.
  // Gated on isRealTestSlug so a Test-of-Choice class (whose class.testId is
  // the fvd-unmatched:9999 sentinel, #2251) never trips this: klass.testId
  // there was never a real per-ride hint to compare against in the first
  // place (see #462's comment above and classSchema's own testId comment).
  const classTestId = klass?.testId ?? null;
  const classTestMismatch =
    isRealTestSlug(classTestId) &&
    !!manualTestId &&
    manualTestId !== classTestId;
  const classExpectedTestName =
    resolveCatalogTestName(classTestId, catalogDefsById, fallbackTestName(classTestId)) ??
    classTestId;
  const chosenTestName =
    resolveCatalogTestName(manualTestId, catalogDefsById, fallbackTestName(manualTestId)) ??
    manualTestId;

  const requestAddEntity = (kind, value) =>
    setAddEntity({ open: true, kind, value: value.slice(0, MAX_ENTITY_NAME) });

  const confirmAddEntity = () => {
    const name = addEntity.value.trim();
    if (!name) return;
    if (addEntity.kind === "horse") {
      setAddedHorses((prev) => (prev.includes(name) ? prev : [...prev, name]));
      setManualHorse(name);
    } else {
      setAddedRiders((prev) => (prev.includes(name) ? prev : [...prev, name]));
      setManualRider(name);
    }
    setAddEntity({ open: false, kind: null, value: "" });
  };

  const submitManual = () => {
    const n = Number(manualCompetitor);
    if (!Number.isFinite(n) || n <= 0 || !manualTestId) {
      setManualError(true);
      return;
    }
    // #2103 — the mismatch is a warning, never a hard block (#386/#435): this
    // only requires the explicit tap, it never refuses the create itself.
    if (classTestMismatch && !manualTestAck) {
      setManualError(true);
      return;
    }
    const created = addManualRide(classId, {
      competitorNumber: String(n),
      testId: manualTestId,
      horse: manualHorse,
      rider: manualRider,
      // #1286 — the session's selected show; without it the ride's showId is
      // dropped and the announcer's ridesByShow query never returns it.
      showId: selectedShow?.id,
    });
    if (!created) {
      setManualError(true);
      return;
    }
    // addManualRide already cached the ride + queued a createRide op and, if
    // online, kicked a flush (#422). The scribe isn't blocked either way.
    setManualOpen(false);
    navigate(`/rides/${created.id}/score`);
  };

  const completedCount = useMemo(
    () => rides.filter((r) => r.status === RIDE_STATUSES.COMPLETED).length,
    [rides],
  );

  const visibleRides = useMemo(() => {
    // #460 — scratched rides are a STATUS, not a deletion: they always stay
    // in the list (never filtered out), only completed rides are hideable.
    const filtered = showCompleted
      ? rides
      : rides.filter((r) => r.status !== RIDE_STATUSES.COMPLETED);
    // #632 — strictly by scheduled time, soonest first; no more status
    // grouping (an in-progress ride no longer jumps ahead of an earlier,
    // not-yet-started one). Ties (or a missing scheduledTime, which sorts
    // last) break on rideNumber.
    return sortByScheduledTime(filtered, (a, b) => (a.rideNumber ?? 0) - (b.rideNumber ?? 0));
  }, [rides, showCompleted]);

  const crumbs = [
    { label: t("classes.title"), to: "/classes" },
    // #927 — class number alongside the name, same convention as ClassList.
    { label: klass ? classNumberLabel(klass) : "…" },
  ];

  return (
    <AppShell
      sessionSubtitle={klass ? classNumberLabel(klass) : undefined}
      classId={classId}
    >
      <PageBreadcrumbs items={crumbs} />

      {/* #714 — ride list not yet loaded: shaped skeleton rows instead of a
          bare spinner.
          #2678 PATH 1 / #2821 — gated on `settled`, not the raw `loading`
          flag: `loading` never resets to true on a classId change (#2162),
          so it can read `false` while `klass`/`rides` still hold the
          PREVIOUS class's settled answer. `settled` (useCachedResource,
          common-fe) resets to `false` on a genuine deps change (unless the
          new classId already has a warm-cache hit, itself a real
          synchronous settle), so this now holds the skeleton until the
          CURRENT classId has its own answer instead of briefly showing the
          wrong class. */}
      {!settled && (
        <Box sx={{ mt: 2 }}>
          <TableSkeleton rows={6} columns={2} />
        </Box>
      )}

      {settled && klass && (
        <Box sx={{ mb: 3 }}>
          <Stack
            direction="row"
            alignItems="flex-end"
            justifyContent="space-between"
            spacing={2}
            flexWrap="wrap"
          >
            <Box>
              {/* #1761 — a scribe who opened the split section instead of the
                  base class (identical "N. Name" text, #883) sees the same
                  "Split/RO" chip here that the class list and search show. */}
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                <Typography variant="h5" fontWeight={700}>
                  {classNumberLabel(klass)}
                </Typography>
                <SplitClassChip klass={klass} testId="ride-list-split-chip" />
              </Stack>
              <Stack
                direction="row"
                spacing={1.5}
                sx={{ mt: 0.5, color: "text.secondary" }}
                divider={<span>•</span>}
              >
                <Typography variant="body2">
                  {formatVenueTime(klass.scheduledTime)}
                </Typography>
                {klass.ring && (
                  <Typography variant="body2">{klass.ring}</Typography>
                )}
                <Typography variant="body2">{rides.length} rides</Typography>
                {/* #190 — N completed count. */}
                <Typography variant="body2">
                  {completedCount} completed
                </Typography>
              </Stack>
            </Box>
            <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
              {/* #203 — surface a sheet by competitor ID when the
                  ride isn't on the scheduled list. #2976 — disabled once the
                  class is PLACED (results locked); a Tooltip wraps the
                  disabled Button (a disabled native button fires no pointer
                  events MUI's Tooltip listens for) so the scribe still sees
                  WHY, rather than the control just vanishing — the #386
                  emergency path stays visible and discoverable for every
                  other class status. */}
              <Tooltip
                title={manualRideAllowed ? "" : t("classes.manualRideLockedTooltip")}
              >
                <span>
                  <Button
                    size="small"
                    variant="outlined"
                    color="inherit"
                    onClick={openManual}
                    disabled={!manualRideAllowed}
                    data-testid={`add-manual-ride-button-${klass.id}`}
                  >
                    Add Ride Manually
                  </Button>
                </span>
              </Tooltip>
              {/* #2109 (docs/score/ring-truth.md rule 4) — the current
                  indicator + explicit End action, rendered whenever THIS
                  class is the ring's current occupant, with NO COMPLETE/
                  PLACED suppression: deriveClassStatus flips to COMPLETE
                  the instant the last ride goes FINAL, which is exactly
                  when a scribe wants to press End (judge finding, #2109
                  review) — gating End on classStatus the same way Start is
                  gated would make the button vanish right before it's
                  needed. End is already scoped tightly enough by
                  isClassCurrentInRing alone: nothing to end once the ring
                  record no longer names this class. No confirmation, no
                  loading gate: useRingState.endClass swallows its own
                  failures (AC7 of #1935, unchanged here) and additionally
                  no-ops (server round trip still fires, UI still re-reads)
                  if the ring has moved on since this screen last fetched
                  ring state — a stale screen must never clobber a
                  DIFFERENT scribe's live class in the same ring (rule 5). */}
              {klass.ring && isClassCurrentInRing && (
                <Stack direction="row" spacing={1}>
                  <Chip
                    size="small"
                    color="success"
                    variant="outlined"
                    data-testid={`class-ring-current-${klass.id}`}
                    label={t("classes.ringState.current", { ring: klass.ring })}
                  />
                  <Button
                    size="small"
                    variant="outlined"
                    color="inherit"
                    data-testid={`end-class-button-${klass.id}`}
                    onClick={() => ringState.endClass(klass.ring, klass.id)}
                  >
                    {t("classes.ringState.endClass")}
                  </Button>
                </Stack>
              )}
              {/* #2081 (docs/score/ring-state.md) — Start class, moved here
                  from the class list (#1935), immediately to the right of
                  Add Ride Manually. Rendered only for a class that carries
                  a ring, is NOT already the ring's current occupant (the
                  block above owns that case), and is not COMPLETE/PLACED
                  (AC4's overwrite is silent/unconfirmed, so offering it on
                  a finished class would let a stray tap yank the ring away
                  from whatever is actually running now). No confirmation,
                  no loading gate, no error UI — useRingState.startClass
                  swallows its own failures. */}
              {klass.ring &&
                !isClassCurrentInRing &&
                classStatus !== DERIVED_CLASS_STATUSES.COMPLETE &&
                classStatus !== DERIVED_CLASS_STATUSES.PLACED && (
                  <Button
                    size="small"
                    variant="outlined"
                    color="inherit"
                    data-testid={`start-class-button-${klass.id}`}
                    onClick={() => ringState.startClass(klass.ring, klass.id)}
                  >
                    {t("classes.ringState.startClass")}
                  </Button>
                )}
              {/* #189 — toggle to reveal/hide completed rides. */}
              {completedCount > 0 && (
                <Button
                  size="small"
                  variant="outlined"
                  color="inherit"
                  onClick={() => setShowCompleted((v) => !v)}
                >
                  {showCompleted ? "Hide completed" : "Show completed"}
                </Button>
              )}
            </Stack>
          </Stack>
        </Box>
      )}

      {/* #2783 (bug #257) — the confirmation the dead scoresheet never gave:
          names the ride and what was done to it. */}
      {termConfirmation && (
        <Alert severity="success" data-testid="ride-term-confirmation" sx={{ mb: 2 }}>
          {t("rides.termConfirmation", {
            number: termConfirmation.number,
            reason: termConfirmation.reason,
          })}
        </Alert>
      )}

      <Stack spacing={1.5}>
        {visibleRides.map((ride) => {
          const isScratched = ride.status === RIDE_STATUSES.SCRATCHED;
          return (
            <Card
              key={ride.id}
              variant="outlined"
              sx={{
                position: "relative",
                borderRadius: 3,
                opacity: isScratched ? 0.55 : 1,
                "&:hover": isScratched
                  ? {}
                  : { borderColor: "primary.main", boxShadow: 4 },
                transition: "box-shadow 0.2s, border-color 0.2s",
              }}
            >
              {/* #1922 — discard is only ever offered for a manual
                  (emergency-added) ride; a scheduled ride can't be
                  discarded from here at all. Sits outside CardActionArea
                  (stopPropagation on click) so tapping it never navigates
                  into scoring. */}
              {ride.isManual && (
                <IconButton
                  aria-label={t("rides.discard.ariaLabel", { number: rideCnoNumber(ride) })}
                  data-testid="discard-manual-ride"
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    openDiscard(ride);
                  }}
                  sx={{
                    position: "absolute",
                    top: 6,
                    right: 6,
                    zIndex: 1,
                    color: "text.secondary",
                    "&:hover": { color: "error.main" },
                  }}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              )}
              <CardActionArea
                data-testid="ride-card"
                data-scratched={isScratched || undefined}
                disabled={isScratched}
                onClick={() => {
                  // #460 — scoring is disabled for a scratched ride; the
                  // CardActionArea's disabled prop already blocks the native
                  // click, but guard the handler too in case a future
                  // change swaps the affordance.
                  if (isScratched) return;
                  navigate(`/rides/${ride.id}/score`);
                }}
              >
                <CardContent>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    spacing={2}
                  >
                    <Box>
                      <Typography
                        variant="subtitle1"
                        color="text.primary"
                        fontWeight={700}
                      >
                        {t("rides.ridePrefix", { number: rideCnoNumber(ride) })}
                      </Typography>
                      <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={{ xs: 0, sm: 2 }}
                        sx={{ mt: 0.5 }}
                      >
                        <Typography variant="body2">
                          <Box component="span" sx={{ color: "text.secondary", fontWeight: 600 }}>
                            {t("rides.horse")}:
                          </Box>{" "}
                          {ride.horseName}
                        </Typography>
                        <Typography variant="body2">
                          <Box component="span" sx={{ color: "text.secondary", fontWeight: 600 }}>
                            {t("rides.rider")}:
                          </Box>{" "}
                          {ride.riderName}
                        </Typography>
                        {/* #915 / #1664 — resolved per-ride off ride.testId,
                            table cache preferred over the bundled catalog; a
                            Test-of-Choice class can carry a different test
                            per ride, so this must not fall back to any
                            class-level value. */}
                        {rideTestName(ride, catalogDefsById, fallbackTestName(ride.testId)) && (
                          <Typography variant="body2" data-testid="ride-test-name">
                            <Box component="span" sx={{ color: "text.secondary", fontWeight: 600 }}>
                              {t("rides.test")}:
                            </Box>{" "}
                            {rideTestName(ride, catalogDefsById, fallbackTestName(ride.testId))}
                          </Typography>
                        )}
                        <Typography variant="body2">
                          <Box component="span" sx={{ color: "text.secondary", fontWeight: 600 }}>
                            {t("rides.time")}:
                          </Box>{" "}
                          {formatVenueTime(ride.scheduledTime)}
                        </Typography>
                      </Stack>
                      <Typography
                        variant="body2"
                        sx={{
                          mt: 0.75,
                          color: isScoredStatus(ride.status) ? "success.main" : "text.secondary",
                          fontWeight: isScoredStatus(ride.status) ? 600 : 400,
                        }}
                      >
                        {scoreLabel(t, ride)}
                      </Typography>
                    </Box>
                    <RideStatusBadge
                      status={ride.status}
                      // #460 — keep the localized scratched wording (static key).
                      labels={{
                        [RIDE_STATUSES.SCRATCHED]: t("rides.scratched"),
                        // #3088 - static key; the badge itself is score-common's IN_REVIEW.
                        [DERIVED_RIDE_STATUSES.IN_REVIEW]: t("rides.inReview"),
                        [DERIVED_RIDE_STATUSES.NEEDS_SIGNING]: t("rides.needsSigning"),
                      }}
                      sx={{
                        fontWeight: 600,
                        textDecoration: isScratched ? "line-through" : "none",
                      }}
                    />
                  </Stack>
                </CardContent>
              </CardActionArea>
            </Card>
          );
        })}
      </Stack>

      <Dialog
        open={manualOpen}
        onClose={() => setManualOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Add ride manually</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Use this when you need to score a ride that isn&rsquo;t on
            the scheduled list. Competitor ID is required; horse and
            rider names are optional. The new sheet will use this
            class&rsquo;s test.
          </DialogContentText>
          <Stack spacing={1.5}>
            <TextField
              autoFocus
              fullWidth
              required
              label="Competitor ID"
              value={manualCompetitor}
              onChange={(e) => {
                setManualCompetitor(e.target.value.replace(/\D/g, ""));
                setManualError(false);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") submitManual();
              }}
              inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
              error={manualError && !manualCompetitor}
              helperText={
                manualError && !manualCompetitor
                  ? "Enter a positive competitor ID."
                  : " "
              }
              sx={READABLE_TEXTFIELD_SX}
            />
            <TextField
              select
              fullWidth
              required
              label="Test"
              value={manualTestId}
              onChange={(e) => {
                setManualTestId(e.target.value);
                setManualError(false);
                // #2103 — a freshly picked test needs its own fresh
                // acknowledgement; an ack given for a previous pick must
                // never carry over to a different one.
                setManualTestAck(false);
              }}
              disabled={loadingTests}
              error={manualError && !manualTestId}
              helperText={
                manualError && !manualTestId
                  ? "Pick a test."
                  : loadingTests
                    ? "Loading tests…"
                    : " "
              }
              sx={READABLE_TEXTFIELD_SX}
            >
              {(showTests ?? []).map((tst) => (
                <MenuItem key={tst.id} value={tst.id}>
                  {/* #1878 — prefer the catalog's clean name over testCode. */}
                  {tst.name ?? tst.testCode}
                </MenuItem>
              ))}
            </TextField>
            {/* #2103 — visible at pick time, not just discovered later at
                attach time (resolveManualRide's 409, ride.js): the class's
                own testId is only a hint (#435), so this warns and requires
                an explicit tap, it never blocks the create (#386's
                emergency path). isRealTestSlug above already keeps this
                silent for a Test-of-Choice class, where klass.testId is the
                fvd-unmatched sentinel and comparing it means nothing. */}
            {classTestMismatch && (
              <Alert severity="warning" data-testid="manual-ride-test-mismatch-warning">
                This class&rsquo;s test is <strong>{classExpectedTestName}</strong>, but
                you picked <strong>{chosenTestName}</strong>. You can still add this
                ride if that&rsquo;s correct for this competitor.
                <FormControlLabel
                  sx={{ display: "flex", mt: 1 }}
                  control={
                    <Checkbox
                      checked={manualTestAck}
                      onChange={(e) => {
                        setManualTestAck(e.target.checked);
                        setManualError(false);
                      }}
                      data-testid="manual-ride-test-mismatch-ack"
                    />
                  }
                  label="Yes, this is the right test for this ride."
                />
              </Alert>
            )}
            {/* #204 — search existing horses/riders; typing a name with
                no match surfaces an "Add …" option that opens the
                add-new-entity dialog. Length-capped via MAX_ENTITY_NAME. */}
            <EntityField
              label="Horse (optional)"
              options={[...HORSE_NAMES, ...addedHorses]}
              value={manualHorse}
              onChange={setManualHorse}
              onRequestAdd={(v) => requestAddEntity("horse", v)}
            />
            <EntityField
              label="Rider (optional)"
              options={[...RIDER_NAMES, ...addedRiders]}
              value={manualRider}
              onChange={setManualRider}
              onRequestAdd={(v) => requestAddEntity("rider", v)}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={() => setManualOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={submitManual}
            disabled={
              !manualCompetitor ||
              !manualTestId ||
              (classTestMismatch && !manualTestAck)
            }
          >
            Add and score
          </Button>
        </DialogActions>
      </Dialog>

      {/* #204 — add-new-entity dialog, opened when a typed horse/rider
          matches no existing entry. Length-capped to MAX_ENTITY_NAME. */}
      <Dialog
        open={addEntity.open}
        onClose={() => setAddEntity({ open: false, kind: null, value: "" })}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          {addEntity.kind === "horse" ? "Add new horse" : "Add new rider"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            No match was found. Add this{" "}
            {addEntity.kind === "horse" ? "horse" : "rider"} so it can be
            selected for the ride.
          </DialogContentText>
          <TextField
            autoFocus
            fullWidth
            label={addEntity.kind === "horse" ? "Horse name" : "Rider name"}
            value={addEntity.value}
            onChange={(e) =>
              setAddEntity((prev) => ({
                ...prev,
                value: e.target.value.slice(0, MAX_ENTITY_NAME),
              }))
            }
            onKeyDown={(e) => {
              if (e.key === "Enter") confirmAddEntity();
            }}
            inputProps={{ maxLength: MAX_ENTITY_NAME }}
            helperText={`${addEntity.value.length} / ${MAX_ENTITY_NAME}`}
            FormHelperTextProps={{ sx: { textAlign: "right", m: 0, mt: 0.25 } }}
            sx={READABLE_TEXTFIELD_SX}
          />
        </DialogContent>
        <DialogActions>
          <Button
            color="inherit"
            onClick={() => setAddEntity({ open: false, kind: null, value: "" })}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={confirmAddEntity}
            disabled={!addEntity.value.trim()}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>

      {/* #1922 — discard-manual-ride confirm. `discardGuard` (from
          checkManualRideDiscard) decides which body/actions show: a plain
          confirm, an outright refusal (serverId already assigned), or a
          second-tier "will also lose X" confirm when the ride has score
          data. Never mutates until the scribe taps the confirm button. */}
      <Dialog
        open={Boolean(discardTarget)}
        onClose={closeDiscard}
        maxWidth="xs"
        fullWidth
        data-testid="discard-manual-ride-dialog"
      >
        <DialogTitle>{t("rides.discard.title")}</DialogTitle>
        <DialogContent>
          {discardTarget && discardGuard === null && (
            <DialogContentText>&hellip;</DialogContentText>
          )}
          {discardTarget && discardGuard?.ok && (
            <DialogContentText data-testid="discard-body">
              {t("rides.discard.body", {
                number: rideCnoNumber(discardTarget),
                horse: discardTarget.horseName,
                rider: discardTarget.riderName,
              })}
            </DialogContentText>
          )}
          {discardTarget && discardGuard?.reason === "has-server-id" && (
            <Alert severity="warning" data-testid="discard-refused-server-id">
              {t("rides.discard.refusedServerId")}
            </Alert>
          )}
          {discardTarget && discardGuard?.reason === "not-found" && (
            <Alert severity="warning" data-testid="discard-refused-not-found">
              {t("rides.discard.refusedNotFound")}
            </Alert>
          )}
          {discardTarget && discardGuard?.reason === "create-in-flight" && (
            <Alert severity="info" data-testid="discard-refused-in-flight">
              {t("rides.discard.refusedInFlight")}
            </Alert>
          )}
          {discardTarget && discardGuard?.reason === "check-failed" && (
            <Alert severity="warning" data-testid="discard-check-failed">
              {t("rides.discard.checkFailed")}
            </Alert>
          )}
          {discardTarget && discardGuard?.reason === "has-score-data" && (
            <Alert severity="error" data-testid="discard-score-data-warning">
              {t("rides.discard.scoreDataWarning", {
                number: rideCnoNumber(discardTarget),
                horse: discardTarget.horseName,
                rider: discardTarget.riderName,
                detail: [
                  discardGuard.scoreSheetCount
                    ? t("rides.discard.scoreDataScoreSheets", {
                        count: discardGuard.scoreSheetCount,
                      })
                    : null,
                  discardGuard.queuedOpCount
                    ? t("rides.discard.scoreDataQueuedOps", {
                        count: discardGuard.queuedOpCount,
                      })
                    : null,
                ]
                  .filter(Boolean)
                  .join(", "),
              })}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={closeDiscard}>
            {t("rides.discard.cancel")}
          </Button>
          {(discardGuard?.ok || discardGuard?.reason === "has-score-data") && (
            <Button
              color="error"
              variant="contained"
              onClick={confirmDiscard}
              disabled={discarding}
              data-testid="discard-confirm-button"
            >
              {discardGuard.reason === "has-score-data"
                ? t("rides.discard.confirmAnyway")
                : t("rides.discard.confirm")}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </AppShell>
  );
}
